/**
 * Helpers del Briefing Diario — saludo, racha y mensajes contextuales.
 *
 * Diseño: el panel debe sentirse como un asistente que dice al cliente
 * QUÉ tiene que hacer hoy y POR QUÉ vale la pena entrar a la app.
 */

import { supabase } from '@/integrations/supabase/client';

// ─────────────────────────────────────────────────────────────
// Saludo según hora local
// ─────────────────────────────────────────────────────────────

export function greeting(now = new Date()): string {
  const h = now.getHours();
  if (h >= 5 && h < 12) return 'Buenos días';
  if (h >= 12 && h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

// Día de la semana en ASCII como están guardados en client_routines.day_name
// y client_meal_plans.day_of_week ('lunes', 'martes', etc.)
const DAYS_ES = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];

export function dayOfWeekEs(date = new Date()): string {
  return DAYS_ES[date.getDay()];
}

// ─────────────────────────────────────────────────────────────
// Racha: cuántos días consecutivos con actividad incluyendo hoy
//        (si ayer hubo y hoy no, la racha es "ayer" — se mantiene
//        hasta media noche para no penalizar al usuario que apenas
//        está empezando el día).
// ─────────────────────────────────────────────────────────────

/** Devuelve el set de fechas YYYY-MM-DD donde el cliente tuvo al menos
 *  un registro de cualquier tipo. */
async function getActivityDates(userId: string, daysBack = 60): Promise<Set<string>> {
  const since = new Date();
  since.setDate(since.getDate() - daysBack);
  const sinceIso = since.toISOString();

  const [
    { data: foods },
    { data: exercises },
    { data: weights },
    { data: photos },
  ] = await Promise.all([
    supabase.from('food_logs').select('log_date').eq('user_id', userId).gte('created_at', sinceIso),
    supabase.from('exercise_logs').select('log_date').eq('user_id', userId).gte('created_at', sinceIso),
    supabase.from('body_measurements').select('measure_date').eq('user_id', userId).gte('created_at', sinceIso),
    supabase.from('progress_photos').select('created_at').eq('user_id', userId).gte('created_at', sinceIso),
  ]);

  const dates = new Set<string>();
  for (const r of foods ?? [])     dates.add(((r as any).log_date as string).slice(0, 10));
  for (const r of exercises ?? []) dates.add(((r as any).log_date as string).slice(0, 10));
  for (const r of weights ?? [])   dates.add(((r as any).measure_date as string).slice(0, 10));
  for (const r of photos ?? [])    dates.add(((r as any).created_at as string).slice(0, 10));
  return dates;
}

/** Cuenta días consecutivos con actividad hacia atrás desde hoy.
 *  Si no hay nada hoy ni ayer, la racha es 0. */
export async function calcStreak(userId: string): Promise<number> {
  const dates = await getActivityDates(userId, 90);
  if (dates.size === 0) return 0;

  let streak = 0;
  const cursor = new Date();
  // Grace para "hoy todavía no registró nada pero ayer sí" — la racha
  // se considera viva hasta que termine el día.
  const today = cursor.toISOString().slice(0, 10);
  if (!dates.has(today)) cursor.setDate(cursor.getDate() - 1);

  for (let i = 0; i < 60; i++) {
    const key = cursor.toISOString().slice(0, 10);
    if (dates.has(key)) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
}

// ─────────────────────────────────────────────────────────────
// Mensaje motivacional contextual
// ─────────────────────────────────────────────────────────────

export interface DailyContext {
  /** Racha de días consecutivos con cualquier registro (incluido hoy). */
  streak: number;
  /** Calorías consumidas hoy / meta diaria. */
  caloriesPct: number;
  /** ¿Es día de descanso (sin rutina asignada para hoy)? */
  isRestDay: boolean;
  /** ¿Tiene cero registros hoy? */
  zeroToday: boolean;
  /** Hora actual local (0-23). */
  hour: number;
}

/** Pool de frases motivacionales por contexto. Pensadas para que el
 *  cliente sienta que alguien le habla en serio, no un texto genérico
 *  de app fitness. Frases de fuerza, identidad y consistencia. */
const PHRASES: Record<string, string[]> = {
  sin_actividad_temprano: [
    'Lo que haces hoy es la diferencia entre seguir igual o ser otro. Decide.',
    'Nadie te va a empujar al siguiente nivel. Empieza tú.',
  ],
  sin_actividad_tarde: [
    'Aún hay horas suficientes. Tu yo de mañana te lo agradecerá.',
    'La consistencia no es perfecta. Es no dejar de aparecer. Aparece hoy.',
  ],
  dia_descanso: [
    'Los músculos no crecen entrenando. Crecen descansando. Hoy es parte del plan.',
    'Recuperar bien es entrenar fuerte. No subestimes el descanso.',
  ],
  racha_alta: [
    '{N} días seguidos. Esto ya no es esfuerzo, es quien eres.',
    'Llevas {N} días. La disciplina dejó de ser dura — se volvió identidad.',
  ],
  racha_media: [
    '{N} días seguidos. Así se construye lo que otros solo desean.',
    'Vas {N} días. Cada día más cerca de que esto sea automático.',
  ],
  bajo_calorias_tarde: [
    'Tu cuerpo necesita combustible para reconstruirse. No le falles ahora.',
    'El plan no funciona si te lo saltas. Aliméntate como el atleta que estás siendo.',
  ],
  pasado_calorias: [
    'Un día no rompe nada. Lo que rompe es no volver mañana.',
    'Hoy te excediste. Mañana decides quién manda: tú o el impulso.',
  ],
  va_bien: [
    'Estás haciendo lo que la mayoría dice que va a hacer y nunca hace.',
    'Vas excelente. Esto se siente bien porque está funcionando.',
  ],
  default_neutro: [
    'El progreso no aparece. Lo construyes una decisión a la vez.',
    'No tienes que sentirte motivado para cumplir. Tienes que cumplir para sentirte motivado.',
    'Pequeñas acciones diarias construyen versiones extraordinarias. Hoy suma.',
  ],
};

/** Elige frase estable por día + contexto. Hash simple para evitar
 *  que cada render cambie la frase (molesto) pero rotar día a día. */
function pickPhrase(category: string): string {
  const list = PHRASES[category] ?? PHRASES.default_neutro;
  const today = new Date().toISOString().slice(0, 10);
  let hash = 0;
  for (const ch of today + category) hash = ((hash * 31) + ch.charCodeAt(0)) >>> 0;
  return list[hash % list.length];
}

export function motivationalMessage(ctx: DailyContext): string {
  let category: string;

  if (ctx.isRestDay) {
    category = 'dia_descanso';
  } else if (ctx.zeroToday && ctx.hour >= 18) {
    category = 'sin_actividad_tarde';
  } else if (ctx.zeroToday) {
    category = 'sin_actividad_temprano';
  } else if (ctx.streak >= 7) {
    category = 'racha_alta';
  } else if (ctx.streak >= 3) {
    category = 'racha_media';
  } else if (ctx.caloriesPct < 50 && ctx.hour >= 16) {
    category = 'bajo_calorias_tarde';
  } else if (ctx.caloriesPct > 100) {
    category = 'pasado_calorias';
  } else if (ctx.caloriesPct >= 70) {
    category = 'va_bien';
  } else {
    category = 'default_neutro';
  }

  return pickPhrase(category).replace('{N}', String(ctx.streak));
}
